import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, CheckCircle2, Truck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const TRANSMISSIONS = ["Автоматична", "Ръчна", "Полуавтоматична", "Без значение"];
const FUELS = ["Дизел", "Бензин", "Хибрид", "Електрически", "LPG", "Без значение"];
const BODIES = ["Седан", "Купе", "Комби", "Хечбек", "SUV", "Кабриолет", "Ван", "Пикап"];

export default function ImportRequestPage() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        desired_car: "",
        brand: "",
        model: "",
        year_from: "",
        year_to: "",
        transmission: "",
        fuel: "",
        body_style: "",
        color: "",
        max_mileage: "",
        budget_min: "",
        budget_max: "",
        extras: "",
        timeline: "",
        notes: "",
    });
    const [status, setStatus] = useState({ loading: false, ok: false, error: "" });

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, ok: false, error: "" });
        try {
            const payload = { ...form };
            // Convert numeric strings to numbers, drop empty strings
            ["year_from", "year_to", "max_mileage", "budget_min", "budget_max"].forEach((k) => {
                payload[k] = payload[k] === "" ? null : Number(payload[k]);
            });
            Object.keys(payload).forEach((k) => {
                if (payload[k] === "" || payload[k] === null) delete payload[k];
            });
            // Required:
            payload.desired_car = form.desired_car;
            await api.post("/import-requests", payload);
            setStatus({ loading: false, ok: true, error: "" });
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            setStatus({
                loading: false,
                ok: false,
                error: formatApiError(err?.response?.data?.detail) || err.message,
            });
        }
    };

    if (status.ok) {
        return (
            <main className="relative pt-32 pb-24 min-h-screen flex items-center justify-center px-6">
                <SuccessCard
                    title="Заявката е получена!"
                    text="Благодарим Ви! Ще се свържем с Вас до 24 часа за уточняване на детайлите по вноса."
                />
            </main>
        );
    }

    return (
        <main className="relative pt-32 pb-24 min-h-screen" data-testid="import-page">
            <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
                <Link to="/" className="inline-flex items-center gap-2 text-ink-muted hover:text-gold text-sm mb-8">
                    <ArrowLeft size={16} /> Назад към сайта
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="mb-10"
                >
                    <span className="block text-gold text-[11px] uppercase tracking-[0.32em] mb-4">
                        Услуги · Внос по поръчка
                    </span>
                    <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">
                        Внос на автомобил по поръчка
                    </h1>
                    <p className="text-ink-muted max-w-2xl">
                        Опишете желания от Вас автомобил и нашите експерти ще го намерят и доставят
                        с проверена история, документи и спокойствие за дълъг период.
                    </p>
                </motion.div>

                <form
                    onSubmit={submit}
                    className="bg-surface border border-line rounded-3xl p-8 lg:p-10 shadow-soft space-y-6"
                    data-testid="import-form"
                >
                    {/* Personal */}
                    <SectionTitle icon={<Truck size={16} />} label="Контактна информация" />
                    <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Име" value={form.name} onChange={(v) => set("name", v)} testid="im-name" required />
                        <Field label="Телефон" value={form.phone} onChange={(v) => set("phone", v)} testid="im-phone" required />
                        <Field label="Имейл" type="email" value={form.email} onChange={(v) => set("email", v)} testid="im-email" required />
                        <Field label="Желан срок за доставка" placeholder="напр. до 1 месец" value={form.timeline} onChange={(v) => set("timeline", v)} testid="im-timeline" />
                    </div>

                    {/* Car details */}
                    <SectionTitle label="Желан автомобил" />
                    <Field label="Описание на желания автомобил" placeholder="напр. BMW X5 M Sport, 4x4, кожен салон" value={form.desired_car} onChange={(v) => set("desired_car", v)} testid="im-desired" required />
                    <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Марка" value={form.brand} onChange={(v) => set("brand", v)} testid="im-brand" />
                        <Field label="Модел" value={form.model} onChange={(v) => set("model", v)} testid="im-model" />
                        <Field label="Година от" type="number" value={form.year_from} onChange={(v) => set("year_from", v)} testid="im-year-from" />
                        <Field label="Година до" type="number" value={form.year_to} onChange={(v) => set("year_to", v)} testid="im-year-to" />
                        <Select label="Скоростна кутия" value={form.transmission} options={TRANSMISSIONS} onChange={(v) => set("transmission", v)} testid="im-transmission" />
                        <Select label="Гориво" value={form.fuel} options={FUELS} onChange={(v) => set("fuel", v)} testid="im-fuel" />
                        <Select label="Тип каросерия" value={form.body_style} options={BODIES} onChange={(v) => set("body_style", v)} testid="im-body" />
                        <Field label="Цвят" value={form.color} onChange={(v) => set("color", v)} testid="im-color" />
                        <Field label="Максимален пробег (км)" type="number" value={form.max_mileage} onChange={(v) => set("max_mileage", v)} testid="im-mileage" />
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Бюджет от (BGN)" type="number" value={form.budget_min} onChange={(v) => set("budget_min", v)} testid="im-budget-min" />
                            <Field label="Бюджет до (BGN)" type="number" value={form.budget_max} onChange={(v) => set("budget_max", v)} testid="im-budget-max" />
                        </div>
                    </div>

                    <TextArea label="Допълнителни екстри" placeholder="напр. панорама, кожа, навигация, теглич" value={form.extras} onChange={(v) => set("extras", v)} testid="im-extras" />
                    <TextArea label="Бележки" value={form.notes} onChange={(v) => set("notes", v)} testid="im-notes" />

                    {status.error && (
                        <div className="text-sm text-red-600" data-testid="im-error">{status.error}</div>
                    )}

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={status.loading}
                            className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-champagne font-medium px-10 py-4 rounded-full transition-all disabled:opacity-60 shadow-gold"
                            data-testid="im-submit"
                        >
                            {status.loading ? "Изпращане…" : (<>Изпрати заявка <Send size={16} /></>)}
                        </button>
                    </div>
                </form>
            </div>
        </main>
    );
}

// Shared helpers ---------------------------------------------------------

export function Field({ label, value, onChange, type = "text", testid, required, placeholder }) {
    return (
        <label className="block bg-bg border border-line rounded-xl px-5 py-3 hover:border-gold/40 transition-all">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">
                {label} {required && "*"}
            </span>
            <input
                type={type}
                required={required}
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="w-full mt-1 bg-transparent text-sm focus:outline-none placeholder:text-ink-subtle"
                data-testid={testid}
            />
        </label>
    );
}

export function TextArea({ label, value, onChange, testid, placeholder, required }) {
    return (
        <label className="block bg-bg border border-line rounded-xl px-5 py-3 hover:border-gold/40 transition-all">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">
                {label} {required && "*"}
            </span>
            <textarea
                rows={3}
                required={required}
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="w-full mt-1 bg-transparent text-sm focus:outline-none resize-none placeholder:text-ink-subtle"
                data-testid={testid}
            />
        </label>
    );
}

export function Select({ label, value, options, onChange, testid }) {
    return (
        <label className="block bg-bg border border-line rounded-xl px-5 py-3 hover:border-gold/40 transition-all">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full mt-1 bg-transparent text-sm focus:outline-none"
                data-testid={testid}
            >
                <option value="">Изберете…</option>
                {options.map((o) => (
                    <option key={o} value={o}>{o}</option>
                ))}
            </select>
        </label>
    );
}

export function SectionTitle({ label, icon }) {
    return (
        <div className="flex items-center gap-3 border-b border-line pb-2 pt-2 text-gold">
            {icon}
            <span className="text-[11px] uppercase tracking-[0.32em]">{label}</span>
        </div>
    );
}

export function SuccessCard({ title, text }) {
    return (
        <div className="max-w-md text-center bg-surface border border-line rounded-3xl px-10 py-12 shadow-soft" data-testid="success-card">
            <div className="w-16 h-16 mx-auto rounded-full bg-gold/10 border border-gold/30 text-gold flex items-center justify-center mb-6">
                <CheckCircle2 size={32} />
            </div>
            <h2 className="font-display text-3xl mb-3">{title}</h2>
            <p className="text-ink-muted mb-8">{text}</p>
            <Link to="/" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-champagne px-7 py-3 rounded-full transition-all">
                Към началната страница
            </Link>
        </div>
    );
}
